import { format } from "date-fns";
import type { MarketplaceOrder, MarketplaceCategory, OrderStatus } from "../types";
import { CATEGORY_EMOJI, koinsToEur } from "../constants";
import { CheckCircle, XCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { dateFnsLocale } from "../../../i18n/date";

const STATUS_CONFIG: Record<
  OrderStatus,
  { labelKey: string; className: string }
> = {
  PENDING: {
    labelKey: "orders.statusPending",
    className: "bg-warning/10 text-warning",
  },
  PROCESSING: {
    labelKey: "orders.statusProcessing",
    className: "bg-secondary/10 text-secondary",
  },
  FULFILLED: {
    labelKey: "orders.statusFulfilled",
    className: "bg-success/10 text-success",
  },
  CANCELLED: {
    labelKey: "orders.statusCancelled",
    className: "bg-destructive/10 text-destructive",
  },
  REFUNDED: {
    labelKey: "orders.statusRefunded",
    className: "bg-muted text-muted-foreground",
  },
};

interface OrdersTableProps {
  orders: MarketplaceOrder[];
  total: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onFulfill: (order: MarketplaceOrder) => void;
  onCancel: (order: MarketplaceOrder) => void;
  onViewDetails: (order: MarketplaceOrder) => void;
}

export function OrdersTable({
  orders,
  total,
  isLoading,
  page,
  pageSize,
  onPageChange,
  onFulfill,
  onCancel,
  onViewDetails,
}: OrdersTableProps) {
  const { t, locale } = useLocale();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, total);

  if (isLoading && orders.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-14 animate-pulse rounded-lg border border-border bg-card"
          />
        ))}
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
        <span className="text-4xl">📋</span>
        <p className="mt-3 text-sm font-medium text-foreground">
          {t("orders.emptyTitle")}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("orders.emptyHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("orders.colDate")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("orders.colMember")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("orders.colReward")}
              </th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                {t("orders.colCost")}
              </th>
              <th className="px-4 py-3 text-center font-medium text-muted-foreground">
                {t("orders.colStatus")}
              </th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                {t("orders.colActions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const category =
                (order.item?.category as MarketplaceCategory) ?? "OTHER";
              const emoji = CATEGORY_EMOJI[category] ?? "📦";
              const statusCfg =
                STATUS_CONFIG[order.status] ?? STATUS_CONFIG.PENDING;
              const isPending =
                order.status === "PENDING" || order.status === "PROCESSING";

              const userName = order.user
                ? `${order.user.firstName ?? ""} ${order.user.lastName ?? ""}`.trim() ||
                  order.user.email ||
                  "Unknown"
                : "Unknown";

              const userEmail = order.user?.email ?? "";

              return (
                <tr
                  key={order._id}
                  className="border-b border-border last:border-b-0 hover:bg-muted/30"
                >
                  {/* Date */}
                  <td className="whitespace-nowrap px-4 py-3 text-foreground">
                    {format(new Date(order.createdAt), "PP", {
                      locale: dateFnsLocale(locale),
                    })}
                    <div className="text-[11px] text-muted-foreground">
                      {format(new Date(order.createdAt), "HH:mm")}
                    </div>
                  </td>

                  {/* Member */}
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{userName}</div>
                    {userEmail && (
                      <div className="text-[11px] text-muted-foreground">
                        {userEmail}
                      </div>
                    )}
                  </td>

                  {/* Reward */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span>{emoji}</span>
                      <span className="font-medium text-foreground">
                        {order.item?.name ?? "Unknown Item"}
                      </span>
                    </div>
                  </td>

                  {/* Cost */}
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <span className="font-semibold text-koins">
                      {order.koinsSpent.toLocaleString()}
                    </span>
                    <div className="text-[11px] text-muted-foreground">
                      ≈€{koinsToEur(order.koinsSpent)}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusCfg.className}`}
                    >
                      {t(statusCfg.labelKey)}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isPending && (
                        <>
                          <button
                            type="button"
                            onClick={() => onFulfill(order)}
                            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-success hover:bg-success/10"
                            title={t("adminUI.orders.fulfill")}
                          >
                            <CheckCircle className="h-3.5 w-3.5" />
                            {t("orders.fulfill")}
                          </button>
                          <button
                            type="button"
                            onClick={() => onCancel(order)}
                            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10"
                            title={t("adminUI.orders.cancelRefund")}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                            {t("orders.cancelAction")}
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => onViewDetails(order)}
                        className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        {t("orders.details")}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between border-t border-border px-4 py-2.5">
          <span className="text-xs text-muted-foreground">
            {t("common.showingRange", { start: startRow, end: endRow, total })}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted disabled:opacity-30"
              aria-label={t("common.previousPage")}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onPageChange(i)}
                className={`flex h-7 min-w-[28px] items-center justify-center rounded px-1.5 text-xs font-medium transition-colors ${
                  i === page
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages - 1}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted disabled:opacity-30"
              aria-label={t("common.nextPage")}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
